import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  XMarkIcon,
  TruckIcon,
  CheckIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  MapPinIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import {
  obtenerConfiguracionLogistica,
  guardarConfiguracionLogistica,
  probarCotizacionLogistica
} from '../api/backend_v2';

const LogisticaConfigModal = ({ isOpen, onClose, onSaved, initialTab = 'config' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [simulando, setSimulando] = useState(false);
  const [cpPrueba, setCpPrueba] = useState('5000');
  const [resultadoSimulacion, setResultadoSimulacion] = useState(null);

  const [cpOrigen, setCpOrigen] = useState('1425');
  const [ciudadOrigen, setCiudadOrigen] = useState('CABA');
  const [provinciaOrigen, setProvinciaOrigen] = useState('Buenos Aires');
  const [envioGratisDesde, setEnvioGratisDesde] = useState(0);

  // Andreani state
  const [andreaniEnabled, setAndreaniEnabled] = useState(true);
  const [andreaniModo, setAndreaniModo] = useState('sandbox');
  const [andreaniUsuario, setAndreaniUsuario] = useState('');
  const [andreaniPassword, setAndreaniPassword] = useState('');
  const [andreaniHasPassword, setAndreaniHasPassword] = useState(false);
  const [andreaniCliente, setAndreaniCliente] = useState('');
  const [andreaniContratoDom, setAndreaniContratoDom] = useState('');
  const [andreaniContratoSuc, setAndreaniContratoSuc] = useState('');
  const [andreaniRecargo, setAndreaniRecargo] = useState(0);
  const [mostrarPwdAndreani, setMostrarPwdAndreani] = useState(false);

  // Envia.com state
  const [enviaEnabled, setEnviaEnabled] = useState(true);
  const [enviaModo, setEnviaModo] = useState('sandbox');
  const [enviaApiKey, setEnviaApiKey] = useState('');
  const [enviaHasKey, setEnviaHasKey] = useState(false);
  const [enviaMaskedKey, setEnviaMaskedKey] = useState('');
  const [carriersActivos, setCarriersActivos] = useState(['correoArgentino', 'oca', 'urbano']);
  const [enviaRecargo, setEnviaRecargo] = useState(0);
  const [mostrarKeyEnvia, setMostrarKeyEnvia] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || 'config');
      cargarDatos();
    }
  }, [isOpen, initialTab]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setResultadoSimulacion(null);
      const data = await obtenerConfiguracionLogistica();
      setCpOrigen(data.cp_origen || '1425');
      setCiudadOrigen(data.ciudad_origen || 'CABA');
      setProvinciaOrigen(data.provincia_origen || 'Buenos Aires');
      setEnvioGratisDesde(Number(data.envio_gratis_desde || 0));

      const and = data.andreani || {};
      setAndreaniEnabled(and.enabled !== false);
      setAndreaniModo(and.modo || 'sandbox');
      setAndreaniUsuario(and.usuario || '');
      setAndreaniHasPassword(Boolean(and.has_password));
      setAndreaniPassword('');
      setAndreaniCliente(and.cliente_codigo || '');
      setAndreaniContratoDom(and.contrato_domicilio || '');
      setAndreaniContratoSuc(and.contrato_sucursal || '');
      setAndreaniRecargo(Number(and.recargo_fijo || 0));

      const env = data.envia || {};
      setEnviaEnabled(env.enabled !== false);
      setEnviaModo(env.modo || 'sandbox');
      setEnviaHasKey(Boolean(env.has_api_key));
      setEnviaMaskedKey(env.api_key_masked || '');
      setEnviaApiKey('');
      setCarriersActivos(env.carriers_activos || ['correoArgentino', 'oca', 'urbano']);
      setEnviaRecargo(Number(env.recargo_fijo || 0));
    } catch (err) {
      console.error('Error cargando logística:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'No se pudo cargar la configuración de logística',
        confirmButtonColor: '#0284c7'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleCarrier = (carrierCode) => {
    setCarriersActivos((prev) =>
      prev.includes(carrierCode)
        ? prev.filter((c) => c !== carrierCode)
        : [...prev, carrierCode]
    );
  };

  const handleSimular = async () => {
    try {
      setSimulando(true);
      const res = await probarCotizacionLogistica({
        cp_destino: cpPrueba,
        subtotal: 120000
      });
      setResultadoSimulacion(res);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al cotizar',
        text: err.message || 'No se pudo simular la cotización'
      });
    } finally {
      setSimulando(false);
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      setGuardando(true);
      const payload = {
        cp_origen: cpOrigen.trim(),
        ciudad_origen: ciudadOrigen.trim(),
        provincia_origen: provinciaOrigen.trim(),
        envio_gratis_desde: Number(envioGratisDesde || 0),
        andreani: {
          enabled: andreaniEnabled,
          modo: andreaniModo,
          usuario: andreaniUsuario.trim(),
          password: andreaniPassword.trim() || null,
          cliente_codigo: andreaniCliente.trim(),
          contrato_domicilio: andreaniContratoDom.trim(),
          contrato_sucursal: andreaniContratoSuc.trim(),
          recargo_fijo: Number(andreaniRecargo || 0)
        },
        envia: {
          enabled: enviaEnabled,
          modo: enviaModo,
          api_key: enviaApiKey.trim() || null,
          carriers_activos: carriersActivos,
          recargo_fijo: Number(enviaRecargo || 0)
        }
      };

      await guardarConfiguracionLogistica(payload);

      Swal.fire({
        icon: 'success',
        title: '¡Logística Actualizada!',
        text: 'Las preferencias de Andreani y Envia.com se guardaron correctamente.',
        confirmButtonColor: '#0284c7',
        timer: 2000,
        showConfirmButton: false
      });

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: err.message || 'No se pudo guardar la configuración de envíos'
      });
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget && !guardando) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col my-auto max-h-[90vh] sm:max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-indigo-700 via-blue-700 to-cyan-600 text-white flex items-center justify-between shadow-md shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-md">
              <TruckIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tight flex items-center gap-2">
                Logística y Envíos Multi-Correo
                <span className="text-[10px] bg-white/20 font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  Andreani + Envia.com
                </span>
              </h2>
              <p className="text-xs text-indigo-100 font-medium">
                Configuración de credenciales, tarifario zonal e instructivo paso a paso para la óptica
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Selector de Pestañas: Configuración vs Instructivo Paso a Paso */}
        <div className="px-4 sm:px-6 pt-3 bg-slate-100 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex gap-2 shrink-0 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2.5 text-xs font-black rounded-t-xl flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'config'
                ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 border-indigo-600'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 border-transparent'
            }`}
          >
            <Cog6ToothIcon className="w-4 h-4" />
            <span>1. Configuración y Credenciales</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('instructivo')}
            className={`px-4 py-2.5 text-xs font-black rounded-t-xl flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'instructivo'
                ? 'bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 border-emerald-600'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 border-transparent'
            }`}
          >
            <BookOpenIcon className="w-4 h-4" />
            <span>2. Instructivo Paso a Paso (Cómo dar de alta las cuentas)</span>
            <span className="text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded-full">
              Guía
            </span>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto overscroll-contain space-y-6 flex-1 text-slate-700 dark:text-slate-200">
          {activeTab === 'instructivo' ? (
            /* ── PESTAÑA INSTRUCTIVO PASO A PASO PARA EL NEGOCIO ── */
            <div className="space-y-6 animate-fadeIn">
              {/* Banner Intro */}
              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 flex items-start gap-3">
                <InformationCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-xs space-y-1">
                  <p className="font-black text-emerald-900 dark:text-emerald-200 text-sm">
                    ¿Cómo funciona la integración dual de envíos en tu óptica?
                  </p>
                  <p className="text-emerald-800 dark:text-emerald-300 leading-relaxed">
                    Tu tienda online ya está lista para vender con el <strong>Tarifario Zonal Automático</strong> activado por defecto (calcula el costo según la distancia entre el Código Postal de tu óptica y el del comprador). Cuando quieras emitir etiquetas oficiales en vivo desde tu cuenta, seguí estos pasos para conectar <strong>Envia.com</strong> y/o <strong>Andreani</strong>:
                  </p>
                </div>
              </div>

              {/* Guía 1: Envia.com */}
              <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-700 pb-3">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 px-2.5 py-0.5 rounded-full">
                      Opción Recomendada • Alta en 10 minutos • Sin mínimo mensual
                    </span>
                    <h3 className="text-base font-black text-slate-900 dark:text-white mt-1">
                       Opción A: Cómo dar de alta Envia.com (Correo Argentino, OCA, Urbano y Andreani)
                    </h3>
                  </div>
                  <a
                    href="https://envia.com/es-AR"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shrink-0"
                  >
                    <span>Ir a Envia.com Argentina</span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                  </a>
                </div>

                <ol className="space-y-3 text-xs">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Creá tu cuenta gratuita de empresa/negocio en Envia.com
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        Ingresá a <code className="text-indigo-600 dark:text-indigo-400 font-bold">envia.com/es-AR</code>, hacé clic en <strong>"Crear cuenta"</strong> y completá el nombre de la óptica, email, teléfono y CUIT para facturación.
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Configurá tu dirección de origen y cargá saldo prepago
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        Dentro del panel de Envia.com, andá a <strong>Configuración → Direcciones</strong> y registrá la dirección de tu óptica. Luego, en <strong>Pagos / Saldo</strong>, podés cargar saldo a favor (con Mercado Pago, transferencia o tarjeta); <em>solo se descuenta cuando emitís una etiqueta real</em>.
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Generá tu Clave API (API Token Bearer)
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        En el menú izquierdo de Envia.com, ingresá a <strong>Desarrolladores → Claves API (API Keys)</strong> (o accedé a <code className="font-mono">ship.envia.com/settings/developers</code>). Hacé clic en el botón <strong>"Generar Nueva API Key"</strong> y copiá el código alfanumérico largo que se muestra en pantalla.
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      4
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Pegá el Token en esta ventana y activá Producción
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        Volvé a la pestaña <strong>"1. Configuración y Credenciales"</strong> de esta ventana, pegá tu Token en el campo <strong>API Token Bearer Envia.com</strong>, cambiá el selector a <strong>"API Envia.com Producción"</strong> y presioná <strong>Guardar</strong>.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>

              {/* Guía 2: Andreani Directo */}
              <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-700 pb-3">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300 px-2.5 py-0.5 rounded-full">
                      Opción Corporativa • Para ópticas con cuenta corriente directa
                    </span>
                    <h3 className="text-base font-black text-slate-900 dark:text-white mt-1">
                      Opción B: Cómo dar de alta Andreani Oficial Directo
                    </h3>
                  </div>
                  <a
                    href="https://pymes.andreani.com"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded-xl shrink-0"
                  >
                    <span>Portal Andreani PyMEs</span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                  </a>
                </div>

                <ol className="space-y-3 text-xs">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Solicitá el alta de Cuenta Corriente E-Commerce en Andreani
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        Registrate en <code className="text-cyan-600 dark:text-cyan-400 font-bold">pymes.andreani.com</code> o contactá a un ejecutivo comercial de Andreani indicando que tenés un E-commerce propio desarrollado a medida y necesitás acceso a sus <strong>WebServices REST (API v1/v2)</strong>.
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Recibí los 5 datos técnicos de tu contrato Andreani
                      </p>
                      <p className="text-slate-500 dark:text-slate-400 mb-1.5">
                        El equipo de Integraciones de Andreani te enviará por correo electrónico una planilla con estos 5 datos:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-700 text-[11px]">
                        <div>• <strong>Usuario WebServices:</strong> Tu usuario de API</div>
                        <div>• <strong>Contraseña WebServices:</strong> Clave privada</div>
                        <div>• <strong>Código de Cliente:</strong> Ej. <code className="font-mono">CL0001234</code></div>
                        <div>• <strong>Contrato Domicilio:</strong> Ej. <code className="font-mono">400006709</code></div>
                        <div>• <strong>Contrato Sucursal:</strong> Ej. <code className="font-mono">400006711</code></div>
                      </div>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-600 text-white font-black flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        Cargalos en la sección Andreani Oficial y probá con el Simulador
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        Ingresá los datos en la pestaña <strong>"1. Configuración y Credenciales"</strong>, seleccioná <strong>"API Contrato Corporativo Andreani"</strong> y usá el botón <strong>Simular Cotización</strong> para verificar que devuelva los precios exactos de tu contrato.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>

              {/* Guía 3: Circuito diario en la óptica */}
              <div className="p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-xs space-y-2">
                <p className="font-black text-amber-900 dark:text-amber-300 flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-amber-600" />
                  Resumen del circuito diario cuando entra un pedido web:
                </p>
                <ul className="list-disc list-inside space-y-1 text-amber-800 dark:text-amber-300 text-[11px]">
                  <li>
                    <strong>Si el cliente pagó con Mercado Pago:</strong> El pedido ya aparece como <span className="font-bold">Pagado</span> y el stock del armazón ya se descontó solo.
                  </li>
                  <li>
                    <strong>Si el cliente eligió Transferencia Bancaria:</strong> Cuando recibas el comprobante por WhatsApp, hacé clic en el botón verde <strong>"Acreditar Pago"</strong> en la fila del envío (eso descuenta el stock automáticamente).
                  </li>
                  <li>
                    <strong>Para despachar la caja:</strong> Hacé clic en <strong>"Emitir Etiqueta / Tracking"</strong> en la fila del envío, imprimí el rótulo para pegar en el paquete y tocá el botón de <strong>WhatsApp</strong> para enviarle al cliente su código de seguimiento con un clic.
                  </li>
                </ul>
              </div>
            </div>
          ) : loading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-400">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-indigo-500" />
              <p className="text-sm font-semibold">Cargando configuración de logística...</p>
            </div>
          ) : (
            <form id="form-logistica" onSubmit={handleGuardar} className="space-y-6">
              {/* 1. Origen y Envío Gratis */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-4">
                <h3 className="text-xs font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                  <MapPinIcon className="w-4 h-4" /> 1. Punto de Despacho (Origen) y Envío Gratis
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold mb-1">CP Origen *</label>
                    <input
                      type="text"
                      required
                      value={cpOrigen}
                      onChange={(e) => setCpOrigen(e.target.value)}
                      placeholder="1425"
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-sm font-mono font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold mb-1">Ciudad</label>
                    <input
                      type="text"
                      value={ciudadOrigen}
                      onChange={(e) => setCiudadOrigen(e.target.value)}
                      placeholder="CABA"
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold mb-1">Provincia</label>
                    <input
                      type="text"
                      value={provinciaOrigen}
                      onChange={(e) => setProvinciaOrigen(e.target.value)}
                      placeholder="Buenos Aires"
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold mb-1 flex items-center gap-1">
                      <SparklesIcon className="w-3.5 h-3.5 text-emerald-500" /> Envío Gratis desde ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={envioGratisDesde}
                      onChange={(e) => setEnvioGratisDesde(e.target.value)}
                      placeholder="0 = desactivado"
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-sm font-bold text-emerald-600 dark:text-emerald-400"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-500">
                  Si configurás un monto mayor a $0 en <strong>Envío Gratis desde</strong>, los pedidos que superen ese valor tendrán envío estándar bonificado.
                </p>
              </div>

              {/* 2. Andreani Directo */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2">
                      Andreani Oficial (Opción Mayor Confianza)
                      {andreaniEnabled && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded-full uppercase">
                          Activo
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-slate-500">
                      Ofrece envío a Domicilio, Retiro en Sucursal Andreani y servicio Express Prioritario.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={andreaniEnabled}
                    onChange={(e) => setAndreaniEnabled(e.target.checked)}
                    className="w-5 h-5 accent-indigo-600 rounded cursor-pointer"
                  />
                </div>

                {andreaniEnabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <div>
                      <label className="block text-[11px] font-bold mb-1">Modo de Cotización</label>
                      <select
                        value={andreaniModo}
                        onChange={(e) => setAndreaniModo(e.target.value)}
                        className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs font-semibold"
                      >
                        <option value="sandbox">Tarifario Zonal Automático / Sandbox</option>
                        <option value="produccion">API Contrato Corporativo Andreani</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold mb-1">Usuario API (Opcional)</label>
                      <input
                        type="text"
                        value={andreaniUsuario}
                        onChange={(e) => setAndreaniUsuario(e.target.value)}
                        placeholder="Usuario Andreani"
                        className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-[11px] font-bold mb-1">
                        Contraseña API {andreaniHasPassword && '(Guardada)'}
                      </label>
                      <input
                        type={mostrarPwdAndreani ? 'text' : 'password'}
                        value={andreaniPassword}
                        onChange={(e) => setAndreaniPassword(e.target.value)}
                        placeholder={andreaniHasPassword ? '********' : 'Contraseña'}
                        className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 pr-9 text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => setMostrarPwdAndreani(!mostrarPwdAndreani)}
                        className="absolute right-2.5 top-7 text-slate-400"
                      >
                        {mostrarPwdAndreani ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold mb-1">Código Cliente (CL...)</label>
                      <input
                        type="text"
                        value={andreaniCliente}
                        onChange={(e) => setAndreaniCliente(e.target.value)}
                        placeholder="Ej: CL0001234"
                        className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold mb-1">Contrato Domicilio</label>
                      <input
                        type="text"
                        value={andreaniContratoDom}
                        onChange={(e) => setAndreaniContratoDom(e.target.value)}
                        placeholder="Ej: 400006709"
                        className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold mb-1">Ajuste / Recargo Fijo ($)</label>
                      <input
                        type="number"
                        step="100"
                        value={andreaniRecargo}
                        onChange={(e) => setAndreaniRecargo(e.target.value)}
                        className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs font-bold"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Envia.com Multi-Carrier */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2">
                      Envia.com Multi-Correo (Opción Mejor Precio)
                      {enviaEnabled && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded-full uppercase">
                          Activo
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-slate-500">
                      Integra Correo Argentino, OCA e-Pak y Urbano Express con tarifas competitivas.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={enviaEnabled}
                    onChange={(e) => setEnviaEnabled(e.target.checked)}
                    className="w-5 h-5 accent-indigo-600 rounded cursor-pointer"
                  />
                </div>

                {enviaEnabled && (
                  <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold mb-1">Modo Envia.com</label>
                        <select
                          value={enviaModo}
                          onChange={(e) => setEnviaModo(e.target.value)}
                          className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs font-semibold"
                        >
                          <option value="sandbox">Tarifario Inteligente / Sandbox</option>
                          <option value="produccion">API Envia.com Producción</option>
                        </select>
                      </div>
                      <div className="relative">
                        <label className="block text-[11px] font-bold mb-1 flex items-center justify-between">
                          <span>API Token Bearer Envia.com</span>
                          {enviaHasKey && (
                            <span className="text-[10px] text-emerald-600 flex items-center gap-0.5">
                              <ShieldCheckIcon className="w-3 h-3" /> Cifrado
                            </span>
                          )}
                        </label>
                        <input
                          type={mostrarKeyEnvia ? 'text' : 'password'}
                          value={enviaApiKey}
                          onChange={(e) => setEnviaApiKey(e.target.value)}
                          placeholder={enviaHasKey ? `Actual: ${enviaMaskedKey}` : 'Token Envia.com (Opcional)'}
                          className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 pr-9 text-xs font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarKeyEnvia(!mostrarKeyEnvia)}
                          className="absolute right-2.5 top-7 text-slate-400"
                        >
                          {mostrarKeyEnvia ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                        </button>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold mb-1">Ajuste / Packaging ($)</label>
                        <input
                          type="number"
                          step="100"
                          value={enviaRecargo}
                          onChange={(e) => setEnviaRecargo(e.target.value)}
                          className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-2.5 text-xs font-bold"
                        />
                      </div>
                    </div>

                    {/* Correos habilitados en Envia.com */}
                    <div>
                      <span className="block text-[11px] font-bold mb-1.5">
                        Operadores habilitados en Envia.com:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { code: 'correoArgentino', label: 'Correo Argentino (Mejor Precio)' },
                          { code: 'oca', label: 'OCA e-Pak' },
                          { code: 'urbano', label: 'Urbano Express' }
                        ].map((c) => {
                          const active = carriersActivos.includes(c.code);
                          return (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => toggleCarrier(c.code)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                                active
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                  : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700'
                              }`}
                            >
                              {active ? '✓ ' : '+ '}
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 4. Simulador de Cotización en Vivo */}
              <div className="p-4 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900/50 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <span className="text-xs font-black text-indigo-900 dark:text-indigo-200 block">
                      Simulador de Cotización en Tiempo Real
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Probá cómo verá tu cliente las tarifas hacia cualquier Código Postal
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={cpPrueba}
                      onChange={(e) => setCpPrueba(e.target.value)}
                      placeholder="CP Destino (ej: 5000)"
                      className="w-28 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-mono font-bold"
                    />
                    <button
                      type="button"
                      onClick={handleSimular}
                      disabled={simulando}
                      className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5"
                    >
                      {simulando ? (
                        <>
                          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> Cotizando...
                        </>
                      ) : (
                        'Simular Cotización'
                      )}
                    </button>
                  </div>
                </div>

                {resultadoSimulacion?.opciones && (
                  <div className="pt-2 space-y-1.5">
                    <p className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300">
                      Zona detectada: {resultadoSimulacion.zona} (CP {resultadoSimulacion.cp_destino})
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {resultadoSimulacion.opciones.map((op) => (
                        <div
                          key={op.id}
                          className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-bold block text-slate-800 dark:text-slate-100">
                              {op.servicio}
                            </span>
                            <span className="text-[10px] text-slate-500">{op.dias_estimados}</span>
                          </div>
                          <span className="font-black text-indigo-600 dark:text-indigo-400">
                            ${Number(op.precio).toLocaleString('es-AR')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === 'config' ? 'instructivo' : 'config')}
            className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1.5"
          >
            <BookOpenIcon className="w-4 h-4" />
            {activeTab === 'config' ? 'Ver instructivo paso a paso para obtener credenciales' : 'Volver al formulario de configuración'}
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cerrar
            </button>
            {activeTab === 'config' && (
              <button
                type="submit"
                form="form-logistica"
                disabled={guardando || loading}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-indigo-600/30"
              >
                {guardando ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" /> Guardando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" /> Guardar Configuración de Envíos
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

LogisticaConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  initialTab: PropTypes.string
};

export default LogisticaConfigModal;
